/**
 * Contextual loading copy for capture / embed / Shopify viewer surfaces.
 * Hero keeps its own preview-specific labels in HowItWorksDemoViewer.
 */

/** @typedef {'hero'|'capture'|'embed'|'embedShare'|'shopify'} ViewerLoadingSurface */
/** @typedef {'reconstruction'|'viewerPrep'|'viewerLoad'} ViewerLoadingPhase */

export const VIEWER_LOADING_SURFACES = Object.freeze({
  HERO: 'hero',
  CAPTURE: 'capture',
  EMBED: 'embed',
  EMBED_SHARE: 'embedShare',
  SHOPIFY: 'shopify',
});

/** Multiphase viewer wait model (Phase 1 messaging). */
export const VIEWER_LOADING_PHASE = Object.freeze({
  RECONSTRUCTION: 'reconstruction',
  VIEWER_PREP: 'viewerPrep',
  VIEWER_LOAD: 'viewerLoad',
});

export const VIEWER_PHASE_TITLES = Object.freeze({
  VIEWER_PREP: 'PREPARING VIEWER',
  VIEWER_LOAD: 'LOADING VIEWER',
});

export const VIEWER_PREP_PROGRESS_MESSAGE = 'PREPARING VIEWER';

const VIEWER_PREP_DETAIL_DEFAULT =
  'Optimizing your capture for the web viewer. Large scenes can take a few minutes.';

const SURFACE_COPY = Object.freeze({
  hero: {
    fetching: 'Fetching preview…',
    metadata: 'Preparing preview…',
    downloading: 'Loading 3D scene…',
    preparing: 'Rendering scene…',
    rendering: 'Rendering scene…',
    restoring: 'Restoring viewer…',
    finalizing: 'Finalizing preview…',
    viewerPrep: 'Preparing preview…',
    viewerLoad: 'Loading preview…',
    error: 'Preview unavailable',
  },
  capture: {
    fetching: 'Loading capture…',
    metadata: 'Preparing 3D viewer…',
    downloading: 'Loading the 3D viewer…',
    preparing: 'Starting the 3D viewer…',
    rendering: 'Rendering capture…',
    restoring: 'Restoring viewer…',
    finalizing: 'Preparing web viewer…',
    viewerPrep: VIEWER_PHASE_TITLES.VIEWER_PREP,
    viewerPrepDetail: VIEWER_PREP_DETAIL_DEFAULT,
    viewerLoad: VIEWER_PHASE_TITLES.VIEWER_LOAD,
    viewerLoadDetail: 'Loading the 3D viewer…',
    error: 'Try reloading the page',
  },
  embed: {
    fetching: 'Loading 3D viewer…',
    metadata: 'Preparing 3D viewer…',
    downloading: 'Loading the 3D viewer…',
    preparing: 'Starting the 3D viewer…',
    rendering: 'Rendering 3D view…',
    restoring: 'Restoring viewer…',
    finalizing: 'Preparing web viewer…',
    viewerPrep: VIEWER_PHASE_TITLES.VIEWER_PREP,
    viewerPrepDetail: 'Optimizing this capture for the web viewer. Large scenes can take a few minutes.',
    viewerLoad: VIEWER_PHASE_TITLES.VIEWER_LOAD,
    viewerLoadDetail: 'Loading the 3D viewer…',
    error: 'Could not load this Volugraph',
  },
  embedShare: {
    fetching: 'Loading shared capture…',
    metadata: 'Preparing 3D viewer…',
    downloading: 'Loading the 3D viewer…',
    preparing: 'Starting the 3D viewer…',
    rendering: 'Rendering 3D view…',
    restoring: 'Restoring viewer…',
    finalizing: 'Preparing web viewer…',
    viewerPrep: VIEWER_PHASE_TITLES.VIEWER_PREP,
    viewerPrepDetail: 'Optimizing this shared capture for the web viewer.',
    viewerLoad: VIEWER_PHASE_TITLES.VIEWER_LOAD,
    viewerLoadDetail: 'Loading the 3D viewer…',
    error: 'Could not load this shared capture',
  },
  shopify: {
    fetching: 'Loading product 3D view…',
    metadata: 'Preparing interactive model…',
    downloading: 'Downloading 3D model…',
    preparing: 'Starting viewer…',
    rendering: 'Rendering product view…',
    restoring: 'Restoring viewer…',
    finalizing: 'Finalizing 3D viewer…',
    error: 'Could not load 3D model',
  },
});

function surfaceCopy(surface, key, fallback) {
  const table = SURFACE_COPY[surface] || SURFACE_COPY.capture;
  return table[key] || fallback || table.downloading;
}

/**
 * Title + helper copy for viewer-prep and viewer-load phases.
 * @param {{ phase?: ViewerLoadingPhase, surface?: ViewerLoadingSurface }} params
 * @returns {{ title: string, detail: string } | null}
 */
export function resolveViewerPhaseCopy({
  phase,
  surface = VIEWER_LOADING_SURFACES.CAPTURE,
} = {}) {
  const table = SURFACE_COPY[surface] || SURFACE_COPY.capture;
  if (phase === VIEWER_LOADING_PHASE.VIEWER_PREP) {
    return {
      title: table.viewerPrep || VIEWER_PHASE_TITLES.VIEWER_PREP,
      detail: table.viewerPrepDetail || VIEWER_PREP_DETAIL_DEFAULT,
    };
  }
  if (phase === VIEWER_LOADING_PHASE.VIEWER_LOAD) {
    return {
      title: table.viewerLoad || VIEWER_PHASE_TITLES.VIEWER_LOAD,
      detail: table.viewerLoadDetail || table.downloading,
    };
  }
  return null;
}

/**
 * Resolve a single loading label for a surface + phase.
 * @param {{ surface?: ViewerLoadingSurface, phase?: string }} params
 */
export function resolveViewerLoadingLabel({
  surface = VIEWER_LOADING_SURFACES.CAPTURE,
  phase = 'downloading',
} = {}) {
  const normalizedPhase = String(phase || 'downloading').toLowerCase();
  return surfaceCopy(surface, normalizedPhase, surfaceCopy(surface, 'downloading'));
}

/**
 * User-facing copy for storage.viewerStatusReason (server-side SOG recovery).
 * @param {string | null | undefined} viewerStatusReason
 */
export function resolveViewerStatusReasonCopy(viewerStatusReason) {
  const r = String(viewerStatusReason || '').trim().toLowerCase();
  if (r === 'sog_auto_requeue_exhausted') {
    return 'Viewer encoding timed out after multiple retries. Try reopening the capture or contact support.';
  }
  if (r === 'sog_auto_requeue') {
    return 'Retrying viewer encoding…';
  }
  return null;
}

/**
 * Map generic overlay messages to surface-specific copy (strip arc-reactor / SOG jargon).
 * @returns {{ message: string, preparingMessage: string }}
 */
export function applyViewerLoadingSurfaceCopy(
  surface = VIEWER_LOADING_SURFACES.CAPTURE,
  messages = {},
  { phase = 'downloading' } = {},
) {
  const msg = typeof messages.message === 'string' ? messages.message.trim() : '';
  const prep = typeof messages.preparingMessage === 'string'
    ? messages.preparingMessage.trim()
    : '';

  const lowerMsg = msg.toLowerCase();
  const lowerPrep = prep.toLowerCase();

  if (lowerMsg.includes('restoring viewer')) {
    const restoring = surfaceCopy(surface, 'restoring', msg);
    return { message: restoring, preparingMessage: restoring };
  }
  if (
    lowerMsg.includes('preparing viewer')
    || lowerPrep.includes('preparing viewer')
    || lowerMsg.includes('optimizing your capture')
    || lowerPrep.includes('optimizing')
  ) {
    const phaseCopy = resolveViewerPhaseCopy({
      phase: VIEWER_LOADING_PHASE.VIEWER_PREP,
      surface,
    });
    return {
      message: phaseCopy?.title || msg || prep,
      preparingMessage: phaseCopy?.detail || prep || msg,
    };
  }
  if (
    lowerMsg === 'loading viewer'
    || lowerPrep === 'loading viewer'
    || lowerMsg.includes('loading splat')
  ) {
    const phaseCopy = resolveViewerPhaseCopy({
      phase: VIEWER_LOADING_PHASE.VIEWER_LOAD,
      surface,
    });
    return {
      message: phaseCopy?.title || msg || prep,
      preparingMessage: phaseCopy?.detail || prep || msg,
    };
  }
  if (lowerMsg.includes('finalizing') || lowerPrep.includes('finalizing')) {
    const finalizing = surfaceCopy(surface, 'finalizing', msg || prep);
    return { message: finalizing, preparingMessage: finalizing };
  }
  if (lowerMsg.includes('loading capture')) {
    const fetching = surfaceCopy(surface, 'fetching', msg);
    return { message: fetching, preparingMessage: surfaceCopy(surface, 'preparing', prep || fetching) };
  }
  if (lowerMsg.includes('arc reactor')) {
    return {
      message: surfaceCopy(surface, 'downloading', msg),
      preparingMessage: surfaceCopy(surface, 'preparing', prep),
    };
  }
  if (phase === 'preparing' || lowerMsg.includes('rendering scene') || lowerPrep.includes('rendering scene')) {
    return {
      message: msg || surfaceCopy(surface, 'downloading'),
      preparingMessage: prep || surfaceCopy(surface, phase === 'preparing' ? 'preparing' : 'rendering'),
    };
  }

  if (phase === 'downloading') {
    return {
      message: msg || surfaceCopy(surface, 'downloading'),
      preparingMessage: prep || surfaceCopy(surface, 'preparing'),
    };
  }

  return {
    message: msg || surfaceCopy(surface, 'downloading'),
    preparingMessage: prep || surfaceCopy(surface, 'preparing'),
  };
}

/**
 * Lightweight embed-route copy when metadata is not yet available.
 * @param {{ surface?: ViewerLoadingSurface, resolving?: boolean, hasMountSrc?: boolean, phase?: string, viewerTabReturnRestoring?: boolean }} params
 */
export function resolveEmbedViewerLoadingMessages({
  surface = VIEWER_LOADING_SURFACES.EMBED,
  resolving = false,
  hasMountSrc = false,
  phase = 'downloading',
  viewerTabReturnRestoring = false,
} = {}) {
  if (viewerTabReturnRestoring) {
    const restoring = applyViewerLoadingSurfaceCopy(
      surface,
      { message: 'Restoring viewer…', preparingMessage: 'Restoring viewer…' },
      { phase: 'restoring' },
    );
    return restoring;
  }
  if (resolving) {
    const fetching = surfaceCopy(surface, 'fetching');
    return { message: fetching, preparingMessage: surfaceCopy(surface, 'metadata') };
  }
  if (!hasMountSrc) {
    const metadata = surfaceCopy(surface, 'metadata');
    return { message: metadata, preparingMessage: metadata };
  }
  return applyViewerLoadingSurfaceCopy(
    surface,
    {
      message: surfaceCopy(surface, 'downloading'),
      preparingMessage: surfaceCopy(surface, phase === 'preparing' ? 'preparing' : 'rendering'),
    },
    { phase },
  );
}
